// ADR 1.10.26n §1 — the settings, declared once: every section, and the six
// category pages that hold them.
//
// `SETTINGS_SECTIONS` is 29.9.26ap §3's per-section declaration (moved here
// from the retired `SettingsIndex.tsx`), and `SETTINGS_CATEGORIES` groups those
// ids into pages. Pure data: `app/accountRoutes.tsx` is eager and maps the
// categories into routes, so nothing here may import a component. The section
// ids, their order inside a category and their `titleKey`s are 30.9.26h's
// contract; `settingsCategories.test.ts` pins that the categories partition
// the sections.

/** Every section, in page order: region id and the key its Panel title uses. */
export const SETTINGS_SECTIONS = [
  { id: 'settings-account', titleKey: 'account_settings_account_section' },
  { id: 'settings-subscription', titleKey: 'account_settings_subscription_section' },
  { id: 'settings-usage', titleKey: 'plan_benefits_title' },
  // ADR 30.9.26h §3 — the learner's everyday settings, in Android's root order;
  // 30.9.26i's speech-speed link targets `settings-tutor`, so these ids are a contract.
  { id: 'settings-languages', titleKey: 'settings_language' },
  { id: 'settings-learning', titleKey: 'settings_learning_header' },
  // ADR 20.10.26m §7 — "Sound & haptics", right after Learning.
  { id: 'settings-feedback', titleKey: 'settings_feedback_group' },
  { id: 'settings-tutor', titleKey: 'tutor_settings_title' },
  { id: 'settings-reminder', titleKey: 'settings_daily_reminder' },
  // ADR 17.10.26g §8 — the phones' Show tips switch and Reset Tutorials.
  { id: 'settings-tips', titleKey: 'settings_show_tips' },
  // ADR 30.9.26i §1 — the settings that guard the learner.
  { id: 'settings-accessibility', titleKey: 'settings_accessibility_title' },
  { id: 'settings-theme', titleKey: 'settings_theme' },
  // ADR 7.10.26v §8 — the device's one-page / two-page preference.
  { id: 'settings-pages', titleKey: 'spread_pref_title' },
  { id: 'settings-privacy', titleKey: 'settings_privacy' },
  { id: 'settings-developer', titleKey: 'connections_title' },
  { id: 'settings-danger', titleKey: 'account_settings_danger_zone' },
] as const

export type SectionId = (typeof SETTINGS_SECTIONS)[number]['id']

/** A category's rows: a region id, or the profile-only Install + Sign Out block. */
export type CategoryEntry = SectionId | 'account-actions'

/**
 * §1's table, in list order. Install + Sign Out sit above the Danger Zone, so
 * 30.9.26i's "nobody scrolls past it to leave" still holds inside `profile`.
 */
export const SETTINGS_CATEGORIES = [
  {
    slug: 'profile',
    titleKey: 'account_settings_account_section',
    entries: ['settings-account', 'settings-subscription', 'settings-usage', 'account-actions', 'settings-danger'],
  },
  { slug: 'language', titleKey: 'settings_language', entries: ['settings-languages'] },
  { slug: 'learning', titleKey: 'settings_learning_header', entries: ['settings-learning', 'settings-feedback', 'settings-tutor', 'settings-reminder', 'settings-tips'] },
  { slug: 'appearance', titleKey: 'settings_appearance', entries: ['settings-accessibility', 'settings-theme', 'settings-pages'] },
  { slug: 'privacy', titleKey: 'settings_privacy', entries: ['settings-privacy'] },
  // §1 — the one conditional category, listed only when `integrationsAdmit` holds.
  { slug: 'developer', titleKey: 'connections_title', entries: ['settings-developer'] },
] as const satisfies readonly { slug: string; titleKey: string; entries: readonly CategoryEntry[] }[]

export type SettingsCategory = (typeof SETTINGS_CATEGORIES)[number]
export type CategorySlug = SettingsCategory['slug']

/** The category a slug names, or `undefined` for one no row declares. */
export function categoryBySlug(slug: string): SettingsCategory | undefined {
  return SETTINGS_CATEGORIES.find((category) => category.slug === slug)
}

/** A category's region ids, without the `account-actions` pseudo-entry. */
export function sectionsOf(category: SettingsCategory): SectionId[] {
  return category.entries.filter((entry): entry is SectionId => entry !== 'account-actions')
}

/**
 * Derived, never read from the DOM (29.9.26ap §3): which category owns a
 * section id. The pseudo-entry is not a section, so it has no owner.
 */
export function categoryOf(sectionId: string): CategorySlug | undefined {
  return SETTINGS_CATEGORIES.find((category) => (sectionsOf(category) as string[]).includes(sectionId))?.slug
}
